import React, { useState } from 'react';
import { FileText, Video, Presentation } from 'lucide-react';

interface PitchData {
  elevator_pitch: string;
  value_proposition: string;
  target_market: string;
  competitive_advantage: string;
  pitch_deck_url?: string;
  demo_video_url?: string;
}

interface PitchSectionProps {
  label: string;
  value: PitchData;
  onSave: (pitch: PitchData) => Promise<void>;
  disabled?: boolean;
}

export const PitchSection: React.FC<PitchSectionProps> = ({
  label,
  value,
  onSave,
  disabled = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempPitch, setTempPitch] = useState<PitchData>(value);

  const startEditing = () => {
    if (disabled) return;
    setIsEditing(true);
    setTempPitch({ ...value });
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setTempPitch({ ...value });
  };

  const saveChanges = async () => {
    await onSave(tempPitch);
    setIsEditing(false);
  };

  const updateField = (field: keyof PitchData, newValue: string) => {
    setTempPitch(prev => ({
      ...prev,
      [field]: newValue
    }));
  };

  const hasContent = () => {
    return Object.values(value).some(val => val && val.trim() !== '');
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label}
      </label>

      {!isEditing ? (
        // Mode affichage
        <div
          onClick={startEditing}
          className={`
            min-h-[2.5rem] p-4 border border-gray-300 rounded-lg
            ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}
            transition-colors duration-200
          `}
        >
          {hasContent() ? (
            <div className="space-y-3">
              {value.elevator_pitch && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Elevator Pitch</h4>
                  <p className="text-sm text-gray-600">{value.elevator_pitch}</p>
                </div>
              )}
              
              {value.value_proposition && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Proposition de valeur</h4>
                  <p className="text-sm text-gray-600">{value.value_proposition}</p>
                </div>
              )}
              
              {value.target_market && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Marché cible</h4>
                  <p className="text-sm text-gray-600">{value.target_market}</p>
                </div>
              )}
              
              {value.competitive_advantage && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Avantage concurrentiel</h4>
                  <p className="text-sm text-gray-600">{value.competitive_advantage}</p>
                </div>
              )}
              
              {(value.pitch_deck_url || value.demo_video_url) && (
                <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
                  {value.pitch_deck_url && (
                    <a
                      href={value.pitch_deck_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-1 text-[#197ca8] hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Presentation className="w-4 h-4" />
                      <span className="text-sm">Pitch Deck</span>
                    </a>
                  )}
                  {value.demo_video_url && (
                    <a
                      href={value.demo_video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-1 text-[#197ca8] hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Video className="w-4 h-4" />
                      <span className="text-sm">Vidéo de démonstration</span>
                    </a>
                  )}
                </div>
              )}
            </div>
          ) : (
            <span className="text-gray-400 text-sm flex items-center">
              <FileText className="w-4 h-4 mr-2" />
              Cliquez pour ajouter votre pitch et présentation
            </span>
          )}
        </div>
      ) : (
        // Mode édition
        <div className="space-y-4 border border-gray-300 rounded-lg p-4">
          {/* Elevator Pitch */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Elevator Pitch (30 secondes)
            </label>
            <textarea
              value={tempPitch.elevator_pitch}
              onChange={(e) => updateField('elevator_pitch', e.target.value)}
              placeholder="Décrivez votre entreprise en 30 secondes..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] resize-none"
            />
          </div>

          {/* Proposition de valeur */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Proposition de valeur unique
            </label>
            <textarea
              value={tempPitch.value_proposition}
              onChange={(e) => updateField('value_proposition', e.target.value)}
              placeholder="Qu'est-ce qui vous rend unique ? Quel problème résolvez-vous ?"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] resize-none"
            />
          </div>

          {/* Marché cible */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Marché cible
            </label>
            <textarea
              value={tempPitch.target_market}
              onChange={(e) => updateField('target_market', e.target.value)}
              placeholder="Décrivez votre marché cible et vos clients idéaux..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] resize-none"
            />
          </div>

          {/* Avantage concurrentiel */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Avantage concurrentiel
            </label>
            <textarea
              value={tempPitch.competitive_advantage}
              onChange={(e) => updateField('competitive_advantage', e.target.value)}
              placeholder="Qu'est-ce qui vous différencie de vos concurrents ?"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8] resize-none"
            />
          </div>

          {/* Liens vers documents */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Pitch Deck (URL)
              </label>
              <input
                type="url"
                value={tempPitch.pitch_deck_url || ''}
                onChange={(e) => updateField('pitch_deck_url', e.target.value)}
                placeholder="https://exemple.com/pitch-deck.pdf"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8]"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Vidéo de démonstration (URL)
              </label>
              <input
                type="url"
                value={tempPitch.demo_video_url || ''}
                onChange={(e) => updateField('demo_video_url', e.target.value)}
                placeholder="https://youtube.com/watch?v=..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8]"
              />
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="flex justify-end space-x-2 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={cancelEditing}
              className="px-3 py-2 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={saveChanges}
              className="px-3 py-2 text-sm bg-[#197ca8] text-white rounded hover:bg-[#156a92]"
            >
              Sauvegarder
            </button>
          </div>
        </div>
      )}
    </div>
  );
};